import {Capacitor, registerPlugin} from '@capacitor/core'
import {isFileTransferAvailable} from '@toolify/client/src/plugins/FileTransferPlugin'
import {FilesystemPlugin} from '@toolify/client/src/plugins/FilesystemPlugin'

interface IPickedFile {
  name: string
  size: number
  mimeType: string
  path?: string
  blob?: Blob
  width?: number
  height?: number
  duration?: number
}

interface IPickMediaOptions {
  limit?: number
  skipTranscoding?: boolean
}

interface IPickFilesOptions {
  limit?: number
}

interface IFilePickerPlugin {
  pickMedia(options?: IPickMediaOptions): Promise<{files: IPickedFile[]}>
  pickFiles(options?: IPickFilesOptions): Promise<{files: IPickedFile[]}>
}

const FilePickerPlugin = registerPlugin<IFilePickerPlugin>('FilePicker')

function isFilePickerAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('FilePicker')
}

const NATIVE_FILE_PATH_PROPERTY = 'nativeFilePath'

export function getNativeFilePath(file: File): string | null {
  return (file as File & {[NATIVE_FILE_PATH_PROPERTY]?: string})[NATIVE_FILE_PATH_PROPERTY] ?? null
}

// A zero-byte File carrying the real size and the on-disk path: the bytes never
// enter the webview — the transport uploads them natively from the path.
function createNativeFileHandle(pickedFile: IPickedFile): File {
  const file = new File([], pickedFile.name, {type: pickedFile.mimeType})
  Object.defineProperty(file, 'size', {value: pickedFile.size})
  Object.defineProperty(file, NATIVE_FILE_PATH_PROPERTY, {value: pickedFile.path})
  return file
}

function isPickerCancelledError(error: unknown): boolean {
  return error instanceof Error && error.message.toLowerCase().includes('cancel')
}

function base64ToBytes(base64: string) {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes
}

const READ_FILE_CHUNK_SIZE = 4 * 1024 * 1024
const FETCH_MAX_FILE_SIZE = 16 * 1024 * 1024
const RANGED_FETCH_WINDOW_SIZE = 8 * 1024 * 1024
// The native chunk reader (readFileInChunks) has no backpressure: it queues the
// whole file in app-process memory and gets SIGABRT-killed for very large
// videos (crashed at 426MB, proven fine at 130MB), so cap it well below that.
const CHUNKED_READ_MAX_FILE_SIZE = 200 * 1024 * 1024
// Files assembled in the webview are memory-backed blobs; XHR-uploading one
// crashed the app at 426MB while 130MB was fine. Larger picks fall back to the
// system picker, whose Files are disk-backed and stream on upload.
const WEBVIEW_UPLOAD_MAX_FILE_SIZE = 200 * 1024 * 1024

// Chunked read keeps peak memory at one chunk instead of a whole-file base64
// string, which froze the webview (white screen) for gallery videos.
function readNativeFileBlobInChunks(pickedFile: IPickedFile): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const parts: BlobPart[] = []
    let isSettled = false

    const settleWith = (settle: () => void) => {
      if (isSettled) return
      isSettled = true
      settle()
    }

    void FilesystemPlugin.readFileInChunks({
      path: pickedFile.path,
      chunkSize: READ_FILE_CHUNK_SIZE,
    }, (chunk, error) => {
      if (isSettled) return
      if (error) {
        settleWith(() => reject(error instanceof Error ? error : new Error(String(error))))
        return
      }
      const data = chunk?.data
      if (data instanceof Blob) {
        if (data.size === 0) {
          settleWith(() => resolve(new Blob(parts, {type: pickedFile.mimeType})))
          return
        }
        parts.push(data)
        return
      }
      if (!data) {
        settleWith(() => resolve(new Blob(parts, {type: pickedFile.mimeType})))
        return
      }
      const bytes = base64ToBytes(data)
      parts.push(bytes)
    }).catch(error => {
      settleWith(() => reject(error))
    })
  })
}

// Reads the file through the capacitor:// scheme handler in Range windows: the
// handler seeks and serves only the requested window (206), so neither the app
// process nor the bridge ever holds more than one window.
async function readNativeFileBlobViaRangedFetch(pickedFile: IPickedFile): Promise<Blob> {
  const convertedUrl = Capacitor.convertFileSrc(pickedFile.path)
  const parts: BlobPart[] = []
  let offset = 0
  while (offset < pickedFile.size) {
    const rangeEnd = Math.min(offset + RANGED_FETCH_WINDOW_SIZE, pickedFile.size) - 1
    const response = await fetch(convertedUrl, {
      headers: {
        Range: `bytes=${offset}-${rangeEnd}`,
      },
    })
    if (response.status !== 206) {
      throw new Error(`ranged fetch unsupported (status ${response.status})`)
    }
    const windowBlob = await response.blob()
    if (!windowBlob.size) {
      throw new Error(`ranged fetch returned empty window at offset ${offset}`)
    }
    parts.push(windowBlob)
    offset += windowBlob.size
  }
  if (offset !== pickedFile.size) {
    throw new Error(`ranged fetch size mismatch: got ${offset}, expected ${pickedFile.size}`)
  }
  return new Blob(parts, {type: pickedFile.mimeType})
}

async function readPickedFileBlob(pickedFile: IPickedFile): Promise<Blob> {
  if (pickedFile.blob) {
    return pickedFile.blob
  }
  // Small files: one plain fetch through the scheme handler. Large files: the
  // handler buffers the whole file natively (fails around 400MB), so read them
  // in Range windows instead; the bridge chunk reader stays as a fallback for
  // dev-server mode where convertFileSrc urls are not served at all.
  if (pickedFile.size <= FETCH_MAX_FILE_SIZE) {
    const convertedUrl = Capacitor.convertFileSrc(pickedFile.path)
    try {
      const response = await fetch(convertedUrl)
      if (response.ok) {
        const blob = await response.blob()
        return blob
      }
    } catch {
      // the scheme handler is not serving this url (dev-server mode) - fall through to the bridge chunk reader
    }
  } else {
    try {
      return await readNativeFileBlobViaRangedFetch(pickedFile)
    } catch {
      // the handler did not honour Range - fall through to the bridge chunk reader
    }
  }
  if (pickedFile.size > CHUNKED_READ_MAX_FILE_SIZE) {
    throw new Error(`file too large for bridge chunk read (${pickedFile.size} bytes)`)
  }
  return await readNativeFileBlobInChunks(pickedFile)
}

async function convertPickedFile(pickedFile: IPickedFile): Promise<File> {
  if (!pickedFile.blob && pickedFile.path && pickedFile.size > FETCH_MAX_FILE_SIZE && isFileTransferAvailable()) {
    return createNativeFileHandle(pickedFile)
  }
  const blob = await readPickedFileBlob(pickedFile)
  return new File([blob], pickedFile.name, {type: pickedFile.mimeType})
}

async function showFileTooLargeAlert() {
  // imported lazily so this plugin module can load before pinia/i18n are set up
  const [{useMessages}, {i18n}] = await Promise.all([
    import('@toolify/client/src/modules/notifications/composables/useMessages'),
    import('@toolify/client/src/setup/i18n'),
  ])
  // rejected before the upload started, so the user can pick a smaller file
  useMessages().warning(i18n.global.t('files.fileTooLargeToUpload'), {
    collapseKey: 'files.fileTooLargeToUpload',
  })
}

async function pickAndConvert(label: string, pick: () => Promise<{files: IPickedFile[]}>): Promise<File[] | null> {
  if (!isFilePickerAvailable()) {
    return null
  }
  try {
    const {files} = await pick()
    const oversizedFile = files.find(file => !file.blob && file.size > WEBVIEW_UPLOAD_MAX_FILE_SIZE)
    if (oversizedFile && !isFileTransferAvailable()) {
      await showFileTooLargeAlert()
      return []
    }
    const convertedFiles = await Promise.all(files.map(convertPickedFile))
    return convertedFiles
  } catch (error) {
    if (isPickerCancelledError(error)) {
      return []
    }
    return null
  }
}

export async function pickMediaFilesNatively(multiple: boolean): Promise<File[] | null> {
  return await pickAndConvert('pickMedia', () => FilePickerPlugin.pickMedia({
    limit: multiple ? 0 : 1,
    skipTranscoding: true,
  }))
}

export async function pickDocumentFilesNatively(multiple: boolean): Promise<File[] | null> {
  return await pickAndConvert('pickFiles', () => FilePickerPlugin.pickFiles({
    limit: multiple ? 0 : 1,
  }))
}
